import type { ProvisionRunStatus, ProvisionStep } from '@frontend/types/provision';

function stepStatusClass(status: string): string {
  if (status === 'succeeded' || status === 'skipped_compatible') return 'status-up';
  if (status === 'failed' || status === 'skipped_incompatible') return 'status-down';
  if (status === 'running') return 'status-unknown';
  return 'status-unknown';
}

function stepLabel(step: ProvisionStep): string {
  return step.key.replace(/_/g, ' ');
}

function runStateClass(runState: string): string {
  if (runState === 'completed') return 'status-up';
  if (runState === 'failed') return 'status-down';
  if (runState === 'partial') return 'status-unknown';
  return 'status-unknown';
}

interface ProvisionRunViewProps {
  title?: string;
  status: ProvisionRunStatus | null;
  pollError?: string;
  loading?: boolean;
}

export default function ProvisionRunView({
  title = 'Provision status',
  status,
  pollError,
  loading,
}: ProvisionRunViewProps) {
  if (!status && !pollError && !loading) return null;

  const failed = status?.runState === 'failed';
  const partial = status?.runState === 'partial';

  return (
    <div className="admin-provision-outcome" aria-live="polite">
      <h4>{title}</h4>

      {pollError ? (
        <div className="settings-message settings-error" role="alert">
          {pollError}
        </div>
      ) : null}

      {loading && !status ? (
        <p className="ecosystem-description">Loading provision status…</p>
      ) : null}

      {status ? (
        <>
          {failed ? (
            <div className="settings-message settings-error" role="alert">
              Provisioning failed. Portal policy and artifacts created in this run were rolled back
              when possible. Use correlation id below for server logs.
            </div>
          ) : null}
          {partial ? (
            <div className="settings-message settings-error" role="alert">
              Provisioning finished with partial success. Review steps below and server logs before
              treating the operation as complete.
            </div>
          ) : null}

          <ul className="admin-sync-status-list">
            <li>
              <span className="admin-sync-status-label">Run state</span>
              <span className={`admin-sync-status-value ${runStateClass(status.runState)}`}>
                {status.runState}
              </span>
            </li>
            <li>
              <span className="admin-sync-status-label">Correlation id</span>
              <span className="admin-sync-status-value">
                <code className="admin-inline-code">{status.correlationId}</code>
              </span>
            </li>
            {(status.steps || []).map((step, idx) => (
              <li key={`${step.key}-${step.startedAt || idx}`}>
                <span className="admin-sync-status-label">{stepLabel(step)}</span>
                <span className={`admin-sync-status-value ${stepStatusClass(step.status)}`}>
                  {step.status}
                  {step.code ? ` (${step.code})` : ''}
                </span>
              </li>
            ))}
          </ul>

          {(status.retryHints || []).length > 0 ? (
            <p className="ecosystem-description">
              Retry hints: {(status.retryHints || []).map((h) => h.action || h.step).join(', ')}
            </p>
          ) : null}

          {(status.compensation || []).length > 0 ? (
            <ul className="admin-sync-status-list">
              {(status.compensation || []).map((c) => (
                <li key={c.target}>
                  <span className="admin-sync-status-label">Rollback {c.target}</span>
                  <span
                    className={`admin-sync-status-value ${c.deleted ? 'status-up' : 'status-down'}`}
                  >
                    {c.deleted ? 'removed' : c.reason || c.error || 'skipped'}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
